import { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

interface EFDataPoint { date: string; ef: number; aerobicDecoupling: number; intensityFactor?: number; variabilityIndex?: number; }
interface EfficiencyFactorChartProps { data: EFDataPoint[]; }

export function EfficiencyFactorChart({ data }: EfficiencyFactorChartProps) {
  const chartData = useMemo(() => data.map((d) => ({ ...d, dateLabel: d.date.slice(5) })), [data]);

  if (!chartData.length) {
    return (
      <div className="rounded-xl border border-border/60 p-4">
        <h3 className="text-sm font-semibold mb-3">Efficiency Factor & Decoupling</h3>
        <div className="h-[260px] flex items-center justify-center text-muted-foreground text-sm">
          Complete endurance activities with HR + power/pace data to see efficiency trends.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-border/60 p-4">
      <h3 className="text-sm font-semibold mb-3 truncate">Efficiency Factor & Decoupling</h3>
      <div className="h-[280px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" opacity={0.2} vertical={false} />
            <XAxis dataKey="dateLabel" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} />
            <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={35} />
            <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: 'hsl(var(--muted-foreground))' }} width={35} unit="%" />
            <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))', borderRadius: '8px', fontSize: '12px' }} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Line yAxisId="left" dataKey="ef" stroke="hsl(var(--chart-1))" strokeWidth={1.5} dot={false} name="EF (NP/HR)" />
            <Line yAxisId="right" dataKey="aerobicDecoupling" stroke="hsl(var(--chart-4))" strokeWidth={1.5} dot={false} name="Decoupling %" />
            {chartData[0]?.intensityFactor !== undefined && (
              <Line yAxisId="left" dataKey="intensityFactor" stroke="hsl(var(--chart-2))" strokeWidth={1} dot={false} name="IF" strokeDasharray="4 2" />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="text-[10px] font-mono text-muted-foreground text-center mt-2">Rising EF with low decoupling (&lt;5%) indicates improving aerobic fitness.</p>
    </div>
  );
}
